import React, { memo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaCheck } from 'react-icons/fa';
import { useAnalytics } from 'use-analytics';
import { setCruiseSearchInputs } from '../../../store/slices/tripSearch';
import Dropdown from '../../Dropdown';
import {
  Duration,
  DurationTypes,
  DurationRange,
} from '../../../constants';
import RangeSetting from '../../RangeSetting';

const DurationSearchInput = () => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { duration } = useSelector((store) => store.tripSearch.searchInputs.cruise);

  const getIncrementValue = useCallback((field) => {
    const oldValue = duration[field];
    if (oldValue === null) {
      return undefined;
    }
    const valueTo = duration.to || DurationRange.max;
    let newValue = null;
    if (field === 'to') {
      newValue = oldValue >= DurationRange.max ? null : oldValue + 1;
      return newValue;
    }
    newValue = oldValue + 1;
    if (newValue > valueTo) {
      return undefined;
    }
    return newValue;
  }, [duration]);

  const getDecrementValue = useCallback((field) => {
    const oldValue = duration[field] || DurationRange.max + 1;
    if (oldValue <= DurationRange.min) {
      return undefined;
    }
    const valueFrom = duration.from;
    let newValue = null;
    if (field === 'to') {
      newValue = oldValue === null ? DurationRange.max : oldValue - 1;
      if (newValue < valueFrom) {
        return undefined;
      }
      return newValue;
    }
    return oldValue - 1;
  }, [duration]);

  const handleIncrementNights = (field) => {
    const newValue = getIncrementValue(field);
    if (newValue || newValue === null) {
      dispatch(setCruiseSearchInputs({
        duration: {
          ...duration,
          [field]: newValue,
        },
      }));
    }
  };

  const handleDecrementNights = (field) => {
    const newValue = getDecrementValue(field);
    if (newValue || newValue === null) {
      dispatch(setCruiseSearchInputs({
        duration: {
          ...duration,
          [field]: newValue,
        },
      }));
    }
  };

  const handleDurationChange = (val) => {
    track('searchbar.handleDurationChange', { duration: val });
    dispatch(setCruiseSearchInputs({
      duration: Duration[val],
    }));
  };

  const setRangeValue = (value) => (value === null ? '32+' : value);

  const setDropdownLabel = () => {
    if (duration.value === Duration[6].value) {
      const nightsCount = duration.from === duration.to ? duration.from : `${duration.from} - ${setRangeValue(duration.to)}`;
      const nightsLabel = duration.from === 1 && duration.to === 1 ? 'night' : 'nights';
      return `${nightsCount} ${nightsLabel}`;
    }
    return duration.label;
  };

  return (
    <Dropdown className="w-full h-full">
      <Dropdown.Toggle className="bg-searchInput-0 border border-gray-500 border-opacity-25 px-6 py-3 bg-white w-full h-full cursor-default">
        <span className="font-medium">{setDropdownLabel()}</span>
        <Dropdown.Toggle.Chevron />
      </Dropdown.Toggle>
      <Dropdown.Menu value={duration.value} onChange={handleDurationChange}>
        {Duration.map((d) => (
          <Dropdown.Item key={d.value} label={d.label} value={d.value} variant="select" lowWidth>
            <div className="p-1 self-center justify-self-center coll-span-1">
              {duration.value === d.value && <FaCheck className="ml-1 h-3 mr-4" />}
            </div>
            <button
              type="button"
              onClick={() => handleDurationChange(d.value)}
              className="col-span-4 text-left p-1 py-2 text-sm text-gray-700 focus:outline-none whitespace-no-wrap "
            >
              {d.label}
            </button>
          </Dropdown.Item>
        ))}
        {
          duration.value === Duration[6].value
            ? (
              <>
                {DurationTypes.map(({ type, label, helperText }) => (
                  <Dropdown.Item lowWidth key={type}>
                    <div className="p-1 self-center justify-self-center coll-span-1" />
                    <RangeSetting
                      type={type}
                      label={label}
                      helperText={helperText}
                      onIncrement={handleIncrementNights}
                      onDecrement={handleDecrementNights}
                      value={setRangeValue(duration[type])}
                      lowWidth
                    />
                  </Dropdown.Item>
                ))}
              </>
            )
            : <div />
        }
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default memo(DurationSearchInput);
